import { Link } from 'react-router-dom';
import { useReveal } from '../hooks/useReveal';
import { company } from '../data/company';
import { categories } from '../data/categories';
import { industries } from '../data/industries';
import { downloadFile } from '../utils/utils';
import SectionHeading from '../components/SectionHeading';
import CategoryCard from '../components/CategoryCard';
import FeatureCard from '../components/FeatureCard';
import ResourceCard from '../components/ResourceCard';
import CTASection from '../components/CTASection';
import Icon, { type IconName } from '../components/Icon';
import machiningOil from '../assets/images/machining-oil.jpg';
import refineryPlant from '../assets/images/refinery-plant.jpg';

const features = [
  {
    icon: 'badge' as IconName,
    title: 'Excellence Since 1986',
    description: 'Decades of experience in petroleum refining chemistry, trusted across Indian and global markets.',
  },
  {
    icon: 'shield' as IconName,
    title: 'Consistent Quality & QC',
    description: 'Rigorous quality control and ASTM-tested specifications ensure predictable, repeatable performance.',
  },
  {
    icon: 'globe' as IconName,
    title: 'Reliable Global Supply',
    description: 'Dependable production-plan assurance and supply-chain management for uninterrupted delivery.',
  },
  {
    icon: 'handshake' as IconName,
    title: 'Technical Partnership',
    description: 'Formulator flexibility and hands-on technical support to help you get the chemistry right.',
  },
];

const stats = [
  { value: '1986', label: 'Established' },
  { value: 'Four', label: 'Product families' },
  { value: '25+', label: 'Formulated products' },
  { value: 'Six', label: 'Industries served' },
];

export default function Home() {
  useReveal('home');

  return (
    <div>
      {/* ───────────────── Hero ───────────────── */}
      <section className="bg-surface">
        <div className="container-page grid items-stretch gap-10 py-14 lg:grid-cols-2 lg:gap-14 lg:py-20">
          {/* Text panel — dark text on white, always legible */}
          <div className="flex animate-fade-up flex-col justify-center">
            <div className="kicker">
              <span className="tracking-normal text-secondary-dark">Est. 1986</span>
              <span>Ankleshwar · Gujarat · India</span>
            </div>
            <h1 className="mt-7 text-4xl font-semibold leading-[1.05] text-text-primary sm:text-5xl lg:text-[3.75rem]">
              Your trusted partner in <span className="italic text-primary">petrochemicals</span>
            </h1>
            <p className="mt-7 max-w-xl text-lg leading-8 text-text-secondary">
              {company.legalName} manufactures premium petroleum sulphonates, metalworking fluids,
              textile additives and fuel dyes — engineered for emulsion stability, corrosion control
              and dependable performance.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-6">
              <Link to="/products" className="btn-primary">
                Explore Products <Icon name="arrow" className="h-4 w-4" />
              </Link>
              <Link to="/product-finder" className="btn-link">
                <Icon name="search" className="h-4 w-4" /> Product Finder
              </Link>
            </div>
          </div>

          {/* Image panel — gears photo in full original colour, no text over it */}
          <div className="relative animate-fade-in overflow-hidden rounded-lg ring-1 ring-ink/10">
            <img
              src={machiningOil}
              alt="Cutting fluid flowing over a precision-machined gear"
              className="h-64 w-full object-cover sm:h-80 lg:h-full lg:min-h-[520px]"
              loading="eager"
            />
          </div>
        </div>

        {/* Stats band — dark text on paper */}
        <div className="border-t border-ink/10">
          <div className="container-page grid grid-cols-2 divide-ink/10 py-10 sm:grid-cols-4 sm:divide-x">
            {stats.map((s) => (
              <div key={s.label} className="px-2 first:pl-0 sm:px-6">
                <div className="text-3xl font-semibold text-primary sm:text-4xl">{s.value}</div>
                <div className="mt-2 text-xs uppercase tracking-wider text-text-secondary">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────── Why Daxesh ───────────────── */}
      <section className="container-page py-20 sm:py-28">
        <SectionHeading
          index="01"
          kicker="Why Daxesh"
          title="Chemistry you can build a business on"
          lead="From feedstock to finished additive, we combine technical depth with reliable supply so your formulations perform every time."
        />
        <div className="mt-16 grid gap-x-10 gap-y-12 sm:grid-cols-2 lg:grid-cols-4">
          {features.map((f, i) => (
            <div key={f.title} className="reveal" style={{ transitionDelay: `${i * 80}ms` }}>
              <FeatureCard {...f} index={String(i + 1).padStart(2, '0')} />
            </div>
          ))}
        </div>
      </section>

      {/* ───────────────── Product families ───────────────── */}
      <section className="border-y border-ink/10 bg-surface py-20 sm:py-28">
        <div className="container-page">
          <div className="flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-end">
            <SectionHeading
              index="02"
              kicker="Our Portfolio"
              title="Four families, one trusted source"
              lead="Explore our full catalogue of specialty petrochemical products."
            />
            <Link to="/products" className="btn-link flex-none pb-2">
              View all products <Icon name="arrow" className="h-4 w-4" />
            </Link>
          </div>
          <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {categories.map((c, i) => (
              <div key={c.slug} className="reveal" style={{ transitionDelay: `${i * 80}ms` }}>
                <CategoryCard category={c} index={String(i + 1).padStart(2, '0')} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────── Industries ───────────────── */}
      <section className="container-page py-20 sm:py-28">
        <SectionHeading
          index="03"
          kicker="Applications"
          title="Serving the industries that keep the world moving"
          lead="Our chemistries solve real problems across metalworking, oilfield, mining, textiles and beyond."
        />
        <div className="mt-14 grid gap-x-10 gap-y-10 md:grid-cols-2 lg:grid-cols-3">
          {industries.map((ind, i) => (
            <Link
              key={ind.slug}
              to="/industries"
              className="reveal group flex flex-col border-t border-ink/15 pt-6"
              style={{ transitionDelay: `${(i % 3) * 80}ms` }}
            >
              <div className="flex items-center justify-between">
                <span className="index-num">{String(i + 1).padStart(2, '0')}</span>
                <Icon name={ind.icon as IconName} className="h-6 w-6 text-primary/70" />
              </div>
              <h3 className="mt-5 text-lg font-semibold text-text-primary">{ind.name}</h3>
              <p className="mt-2 flex-auto text-sm leading-6 text-text-secondary">{ind.description}</p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                Learn more <Icon name="arrow" className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* ───────────────── About teaser ───────────────── */}
      <section className="border-t border-ink/10 bg-surface">
        <div className="container-page grid gap-0 lg:grid-cols-2">
          <div className="relative min-h-[340px] overflow-hidden">
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${refineryPlant})` }}
              aria-hidden="true"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-deep/80 via-primary-deep/25 to-transparent" aria-hidden="true" />
            <div className="relative flex h-full flex-col justify-end p-10">
              <p className="max-w-sm text-2xl font-medium leading-snug text-white">
                Based in Ankleshwar, Gujarat — supplying trusted petrochemicals worldwide.
              </p>
            </div>
          </div>
          <div className="px-6 py-16 lg:px-14 lg:py-20">
            <div className="kicker"><span className="tracking-normal text-secondary">04</span><span>About Daxesh</span></div>
            <h2 className="mt-4 text-3xl font-semibold leading-[1.1] text-text-primary sm:text-4xl">
              A name you can trust for quality and reliability
            </h2>
            <p className="mt-6 leading-8 text-text-secondary">
              Since our inception, {company.legalName} has been committed to delivering high-quality
              petroleum-based chemicals from our base in Ankleshwar. We provide customers with premium
              products, advanced technical expertise and dependable service — ensuring exceptional value.
            </p>
            <p className="mt-4 leading-8 text-text-secondary">
              Our petroleum sulphonates, metalworking fluids and specialty additives are manufactured
              with consistent quality and reliability — offering superior emulsion stability,
              anticorrosion properties and predictable performance.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-6">
              <Link to="/about" className="btn-primary">
                Our Story <Icon name="arrow" className="h-4 w-4" />
              </Link>
              <Link to="/contact" className="btn-link">
                Contact us <Icon name="arrow" className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────── Resource hub ───────────────── */}
      <section className="container-page py-20 sm:py-28">
        <SectionHeading index="05" kicker="Resources" title="Everything you need to specify with confidence" />
        <div className="mt-14 grid gap-x-10 gap-y-10 sm:grid-cols-2 lg:grid-cols-4">
          <ResourceCard
            icon="search"
            title="Product Finder"
            description="Filter and search our full catalogue by family, group and application."
            to="/product-finder"
          />
          <ResourceCard
            icon="download"
            title="Product Guide"
            description="Download the complete DDPL product guide with specifications."
            onClick={() => downloadFile(company.productGuide.fileUrl, company.productGuide.fileName)}
          />
          <ResourceCard
            icon="table"
            title="Typical Properties"
            description="ASTM-tested typical properties and spec tables for every product."
            to="/products/petroleum-sulphonates"
          />
          <ResourceCard
            icon="mail"
            title="Talk to an Expert"
            description="Request samples, datasheets or formulation support from our team."
            to="/contact"
          />
        </div>
      </section>

      {/* ───────────────── CTA ───────────────── */}
      <CTASection />
    </div>
  );
}
